import { useState, useEffect, useCallback } from 'react';
import { useUrlState } from '../hooks/useUrlParams';
import { paymentKeyAPI } from '../services/api';
import FilterBar, { SearchField, FilterSelect, CountPill } from '../components/FilterBar';

const styles = {
  container: { width: '100%' },
  header: { marginBottom: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  title: { fontSize: '24px', fontWeight: '700', color: '#1e293b', margin: 0 },
  subtitle: { fontSize: '13px', color: '#64748b', marginTop: '2px' },
  card: { backgroundColor: '#ffffff', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)', overflow: 'hidden' },
  addButton: { padding: '10px 20px', backgroundColor: '#4f46e5', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: '600', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px', whiteSpace: 'nowrap' },
  tableWrap: { overflowX: 'auto' },
  table: { width: '100%', borderCollapse: 'collapse', minWidth: '900px' },
  th: { padding: '12px 16px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px', backgroundColor: '#f8fafc', borderBottom: '1px solid #e2e8f0' },
  thRight: { textAlign: 'right' },
  td: { padding: '14px 16px', borderBottom: '1px solid #f1f5f9', fontSize: '14px', color: '#1e293b', verticalAlign: 'middle' },
  tdRight: { padding: '14px 16px', borderBottom: '1px solid #f1f5f9', textAlign: 'right', verticalAlign: 'middle' },
  appName: { fontWeight: '600', color: '#1e293b' },
  mono: { fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: '13px', color: '#475569' },
  secretCell: { display: 'inline-flex', alignItems: 'center', gap: '8px' },
  statusBadge: { padding: '4px 10px', borderRadius: '12px', fontSize: '11px', fontWeight: '600', textTransform: 'uppercase', cursor: 'pointer', display: 'inline-block' },
  envBadge: { padding: '4px 10px', borderRadius: '12px', fontSize: '11px', fontWeight: '600', letterSpacing: '0.3px', display: 'inline-block' },
  envLive: { backgroundColor: '#eef2ff', color: '#4338ca' },
  envTest: { backgroundColor: '#fef3c7', color: '#92400e' },
  activeStatus: { backgroundColor: '#dcfce7', color: '#166534' },
  inactiveStatus: { backgroundColor: '#fee2e2', color: '#991b1b' },
  actionButtons: { display: 'inline-flex', gap: '6px', justifyContent: 'flex-end' },
  iconButton: { padding: '7px', border: '1px solid #e2e8f0', borderRadius: '8px', backgroundColor: '#fff', cursor: 'pointer', color: '#64748b', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.12s' },
  emptyState: { textAlign: 'center', padding: '80px 20px', color: '#64748b' },
  loadingState: { textAlign: 'center', padding: '80px 20px', color: '#64748b' },
  errorBox: { padding: '12px 16px', backgroundColor: '#fef2f2', border: '1px solid #fecaca', borderRadius: '8px', color: '#dc2626', margin: '16px', fontSize: '14px' },
  pagination: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px', borderTop: '1px solid #e2e8f0', flexWrap: 'wrap', gap: '12px' },
  pageInfo: { fontSize: '13px', color: '#64748b' },
  pageButtons: { display: 'flex', gap: '4px' },
  pageButton: { padding: '6px 12px', border: '1px solid #e2e8f0', borderRadius: '6px', backgroundColor: '#fff', cursor: 'pointer', fontSize: '13px', color: '#475569' },
  pageButtonDisabled: { opacity: 0.5, cursor: 'not-allowed' },

  // Modal
  modal: { position: 'fixed', inset: 0, backgroundColor: 'rgba(15, 23, 42, 0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '16px' },
  modalContent: { backgroundColor: '#fff', borderRadius: '16px', width: '100%', maxWidth: '600px', maxHeight: '92vh', overflow: 'hidden', display: 'flex', flexDirection: 'column', boxShadow: '0 25px 60px rgba(0,0,0,0.25)' },
  modalHeader: { padding: '18px 24px', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  modalTitle: { fontSize: '18px', fontWeight: '700', color: '#1e293b', margin: 0 },
  modalBody: { padding: '20px 24px', overflowY: 'auto', flex: 1 },
  modalFooter: { padding: '14px 24px', borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'flex-end', gap: '10px', backgroundColor: '#fafbfc' },
  closeBtn: { width: '32px', height: '32px', border: 'none', backgroundColor: 'transparent', cursor: 'pointer', color: '#64748b', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  formGroup: { marginBottom: '16px' },
  label: { display: 'block', fontSize: '13px', fontWeight: '500', color: '#475569', marginBottom: '6px' },
  hint: { fontSize: '11px', color: '#94a3b8', marginTop: '5px', display: 'block' },
  input: { width: '100%', padding: '10px 14px', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '14px', outline: 'none', boxSizing: 'border-box' },
  secretInputWrap: { position: 'relative' },
  secretInput: { width: '100%', padding: '10px 78px 10px 14px', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '14px', outline: 'none', boxSizing: 'border-box', fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace' },
  revealBtn: { position: 'absolute', right: '6px', top: '50%', transform: 'translateY(-50%)', padding: '5px 10px', border: '1px solid #e2e8f0', borderRadius: '6px', backgroundColor: '#f8fafc', color: '#475569', fontSize: '11px', fontWeight: '600', cursor: 'pointer' },
  twoCol: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' },
  toggleRow: { display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: '#475569', cursor: 'pointer' },
  primaryButton: { padding: '10px 20px', backgroundColor: '#4f46e5', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: '600', cursor: 'pointer' },
  secondaryButton: { padding: '10px 20px', backgroundColor: '#f1f5f9', color: '#475569', border: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: '500', cursor: 'pointer' },
  buttonDisabled: { opacity: 0.6, cursor: 'not-allowed' },
};

const ENVIRONMENTS = ['RELEASE', 'SANDBOX', 'UAT', 'PRODUCTION'];

const defaultForm = {
  appId: '',
  appName: '',
  merchantId: '',
  clientId: '',
  clientSecret: '',
  clientVersion: '1',
  environment: 'RELEASE',
  isActive: true,
};

const PaymentKeys = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [envFilter, setEnvFilter] = useState('');
  const [page, setPage] = useUrlState('page', 1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);

  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ ...defaultForm });
  const [showSecret, setShowSecret] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loadingSecret, setLoadingSecret] = useState(false);
  const [formError, setFormError] = useState('');

  const [confirmDelete, setConfirmDelete] = useState(null);

  const fetchItems = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const params = { page, limit: 20 };
      if (search) params.search = search;
      if (statusFilter) params.isActive = statusFilter === 'active';
      if (envFilter) params.environment = envFilter;
      const response = await paymentKeyAPI.getAll(params);
      const d = response.data || {};
      setItems(Array.isArray(d.data) ? d.data : []);
      setTotalPages(d.pagination?.totalPages || 1);
      setTotal(d.pagination?.total || 0);
    } catch (err) {
      setError(err?.response?.data?.error || 'Failed to fetch payment keys');
    } finally {
      setLoading(false);
    }
  }, [page, search, statusFilter, envFilter]);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  // Debounced search
  useEffect(() => {
    const t = setTimeout(() => {
      if (searchInput !== search) {
        setSearch(searchInput);
        setPage(1);
      }
    }, 350);
    return () => clearTimeout(t);
  }, [searchInput, search, setPage]);

  // Close modal on Escape
  useEffect(() => {
    if (!showModal) return;
    const onKey = (e) => { if (e.key === 'Escape') closeModal(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showModal]);

  const openCreate = () => {
    setEditing(null);
    setForm({ ...defaultForm });
    setShowSecret(false);
    setFormError('');
    setShowModal(true);
  };

  // The list never carries the raw client secret, so editing re-reads the
  // single row to prefill it — otherwise saving would blank out the secret.
  const openEdit = async (item) => {
    setEditing(item);
    setForm({
      appId: item.appId || '',
      appName: item.appName || '',
      merchantId: item.merchantId || '',
      clientId: item.clientId || '',
      clientSecret: '',
      clientVersion: item.clientVersion || '1',
      environment: item.environment || 'RELEASE',
      isActive: !!item.isActive,
    });
    setShowSecret(false);
    setFormError('');
    setShowModal(true);
    setLoadingSecret(true);
    try {
      const res = await paymentKeyAPI.getById(item.id);
      const full = res?.data?.data;
      if (full) setForm(f => ({ ...f, clientSecret: full.clientSecret || '' }));
    } catch (err) {
      setFormError('Could not load the client secret — re-enter it before saving.');
    } finally {
      setLoadingSecret(false);
    }
  };

  const closeModal = () => {
    setShowModal(false);
    setEditing(null);
    setShowSecret(false);
    setFormError('');
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setFormError('');
    if (!form.appId.trim() || !form.appName.trim() || !form.merchantId.trim()
      || !form.clientId.trim() || !form.clientSecret.trim()) {
      setFormError('App ID, app name, merchant ID, client ID and client secret are all required.');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        appId: form.appId.trim(),
        appName: form.appName.trim(),
        merchantId: form.merchantId.trim(),
        clientId: form.clientId.trim(),
        clientSecret: form.clientSecret.trim(),
        clientVersion: form.clientVersion.trim() || '1',
        environment: form.environment,
        isActive: !!form.isActive,
      };
      if (editing) {
        await paymentKeyAPI.update(editing.id, payload);
      } else {
        await paymentKeyAPI.create(payload);
      }
      closeModal();
      fetchItems();
    } catch (err) {
      setFormError(err?.response?.data?.error || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!confirmDelete) return;
    try {
      await paymentKeyAPI.delete(confirmDelete.id);
      setConfirmDelete(null);
      fetchItems();
    } catch (err) {
      setError(err?.response?.data?.error || 'Failed to delete');
      setConfirmDelete(null);
    }
  };

  const toggleStatus = async (item) => {
    try {
      await paymentKeyAPI.update(item.id, { isActive: !item.isActive });
      fetchItems();
    } catch (err) {
      setError(err?.response?.data?.error || 'Failed to update');
    }
  };

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>Payment Keys</h1>
          <p style={styles.subtitle}>Payment-gateway credentials — one key per app per environment.</p>
        </div>
        <button style={styles.addButton} onClick={openCreate}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>
          New Key
        </button>
      </div>

      <div style={styles.card}>
        <FilterBar right={<CountPill count={total} noun="key" />}>
          <SearchField
            value={searchInput}
            onChange={setSearchInput}
            placeholder="Search by app ID, app name, merchant ID or client ID..."
            width={320}
          />
          <FilterSelect value={statusFilter} onChange={(v) => { setStatusFilter(v); setPage(1); }} aria-label="Status">
            <option value="">All Statuses</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </FilterSelect>
          <FilterSelect value={envFilter} onChange={(v) => { setEnvFilter(v); setPage(1); }} aria-label="Environment">
            <option value="">All Environments</option>
            {ENVIRONMENTS.map(env => <option key={env} value={env}>{env}</option>)}
          </FilterSelect>
        </FilterBar>

        {error && <div style={styles.errorBox}>{error}</div>}

        {loading ? (
          <div style={styles.loadingState}>Loading...</div>
        ) : items.length === 0 ? (
          <div style={styles.emptyState}>
            <p style={{ fontSize: '15px', margin: 0 }}>No payment keys yet.</p>
            <p style={{ fontSize: '13px', marginTop: '6px' }}>Click "New Key" to add the first one.</p>
          </div>
        ) : (
          <div style={styles.tableWrap}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>App Name</th>
                  <th style={styles.th}>App ID</th>
                  <th style={styles.th}>Merchant ID</th>
                  <th style={styles.th}>Client ID</th>
                  <th style={styles.th}>Client Secret</th>
                  <th style={styles.th}>Ver</th>
                  <th style={styles.th}>Environment</th>
                  <th style={styles.th}>Status</th>
                  <th style={{ ...styles.th, ...styles.thRight }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.map((item) => (
                  <tr key={item.id}>
                    <td style={{ ...styles.td, ...styles.appName }}>{item.appName}</td>
                    <td style={{ ...styles.td, ...styles.mono }}>{item.appId}</td>
                    <td style={{ ...styles.td, ...styles.mono }}>{item.merchantId}</td>
                    <td style={{ ...styles.td, ...styles.mono }}>{item.clientId || <span style={{ color: '#ef4444' }}>—</span>}</td>
                    <td style={{ ...styles.td, ...styles.mono }}>
                      <span style={styles.secretCell} title="Hidden — open Edit to view or change it">
                        {item.clientSecretMasked || <span style={{ color: '#ef4444' }}>—</span>}
                      </span>
                    </td>
                    <td style={{ ...styles.td, ...styles.mono }}>{item.clientVersion}</td>
                    <td style={styles.td}>
                      <span style={{ ...styles.envBadge, ...(item.environment === 'RELEASE' || item.environment === 'PRODUCTION' ? styles.envLive : styles.envTest) }}>
                        {item.environment}
                      </span>
                    </td>
                    <td style={styles.td}>
                      <span
                        style={{ ...styles.statusBadge, ...(item.isActive ? styles.activeStatus : styles.inactiveStatus) }}
                        onClick={() => toggleStatus(item)}
                        title="Click to toggle"
                      >
                        {item.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td style={styles.tdRight}>
                      <div style={styles.actionButtons}>
                        <button
                          style={styles.iconButton}
                          title="Edit"
                          onClick={() => openEdit(item)}
                          onMouseEnter={(e) => { e.currentTarget.style.background = '#eef2ff'; e.currentTarget.style.color = '#4f46e5'; }}
                          onMouseLeave={(e) => { e.currentTarget.style.background = '#fff'; e.currentTarget.style.color = '#64748b'; }}
                        >
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" /><path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" /></svg>
                        </button>
                        <button
                          style={styles.iconButton}
                          title="Delete"
                          onClick={() => setConfirmDelete(item)}
                          onMouseEnter={(e) => { e.currentTarget.style.background = '#fef2f2'; e.currentTarget.style.color = '#dc2626'; e.currentTarget.style.borderColor = '#fecaca'; }}
                          onMouseLeave={(e) => { e.currentTarget.style.background = '#fff'; e.currentTarget.style.color = '#64748b'; e.currentTarget.style.borderColor = '#e2e8f0'; }}
                        >
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6" /><path d="M19 6l-2 14a2 2 0 01-2 2H9a2 2 0 01-2-2L5 6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" /></svg>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {!loading && items.length > 0 && totalPages > 1 && (
          <div style={styles.pagination}>
            <span style={styles.pageInfo}>Page {page} of {totalPages}</span>
            <div style={styles.pageButtons}>
              <button
                onClick={() => setPage(Math.max(1, page - 1))}
                disabled={page <= 1}
                style={{ ...styles.pageButton, ...(page <= 1 ? styles.pageButtonDisabled : {}) }}
              >Prev</button>
              <button
                onClick={() => setPage(Math.min(totalPages, page + 1))}
                disabled={page >= totalPages}
                style={{ ...styles.pageButton, ...(page >= totalPages ? styles.pageButtonDisabled : {}) }}
              >Next</button>
            </div>
          </div>
        )}
      </div>

      {/* Create/Edit modal */}
      {showModal && (
        <div style={styles.modal} onClick={closeModal}>
          <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <form onSubmit={handleSave} style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
              <div style={styles.modalHeader}>
                <h2 style={styles.modalTitle}>{editing ? 'Edit Payment Key' : 'New Payment Key'}</h2>
                <button type="button" onClick={closeModal} style={styles.closeBtn}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
                </button>
              </div>

              <div style={styles.modalBody}>
                {formError && (
                  <div style={{ ...styles.errorBox, margin: '0 0 16px 0' }}>{formError}</div>
                )}

                <div style={styles.formGroup}>
                  <label style={styles.label}>App Name *</label>
                  <input
                    type="text"
                    style={styles.input}
                    value={form.appName}
                    onChange={(e) => setForm(f => ({ ...f, appName: e.target.value }))}
                    placeholder="e.g. VehicleSetu Android"
                    maxLength={200}
                    required
                  />
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label}>App ID *</label>
                  <input
                    type="text"
                    style={styles.input}
                    value={form.appId}
                    onChange={(e) => setForm(f => ({ ...f, appId: e.target.value }))}
                    placeholder="e.g. com.vehiclesetu.app"
                    maxLength={190}
                    required
                  />
                  <span style={styles.hint}>Must be unique — one key set per app.</span>
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label}>Merchant ID *</label>
                  <input
                    type="text"
                    style={styles.input}
                    value={form.merchantId}
                    onChange={(e) => setForm(f => ({ ...f, merchantId: e.target.value }))}
                    placeholder="e.g. MERCHANTUAT"
                    maxLength={190}
                    required
                  />
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label}>Client ID *</label>
                  <input
                    type="text"
                    style={styles.input}
                    value={form.clientId}
                    onChange={(e) => setForm(f => ({ ...f, clientId: e.target.value }))}
                    placeholder="e.g. M224KENDXCRCT_2608071506"
                    maxLength={190}
                    required
                  />
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label}>Client Secret *</label>
                  <div style={styles.secretInputWrap}>
                    <input
                      type={showSecret ? 'text' : 'password'}
                      style={styles.secretInput}
                      value={form.clientSecret}
                      onChange={(e) => setForm(f => ({ ...f, clientSecret: e.target.value }))}
                      placeholder={loadingSecret ? 'Loading…' : 'Paste the gateway client secret'}
                      maxLength={500}
                      autoComplete="new-password"
                      disabled={loadingSecret}
                      required
                    />
                    <button type="button" style={styles.revealBtn} onClick={() => setShowSecret(s => !s)}>
                      {showSecret ? 'Hide' : 'Show'}
                    </button>
                  </div>
                  <span style={styles.hint}>Stored as-is and masked in the list. Treat it as a secret.</span>
                </div>

                <div style={styles.twoCol}>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>Client Version</label>
                    <input
                      type="text"
                      style={styles.input}
                      value={form.clientVersion}
                      onChange={(e) => setForm(f => ({ ...f, clientVersion: e.target.value }))}
                      placeholder="1"
                      maxLength={20}
                    />
                    <span style={styles.hint}>Khali rakho to "1" set thashe.</span>
                  </div>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>Environment</label>
                    <select
                      style={styles.input}
                      value={form.environment}
                      onChange={(e) => setForm(f => ({ ...f, environment: e.target.value }))}
                    >
                      {ENVIRONMENTS.map(env => <option key={env} value={env}>{env}</option>)}
                    </select>
                  </div>
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label}>Status</label>
                  <label style={styles.toggleRow}>
                    <input
                      type="checkbox"
                      checked={form.isActive}
                      onChange={(e) => setForm(f => ({ ...f, isActive: e.target.checked }))}
                    />
                    <span>Active (usable by the app)</span>
                  </label>
                </div>
              </div>

              <div style={styles.modalFooter}>
                <button type="button" onClick={closeModal} style={styles.secondaryButton}>Cancel</button>
                <button
                  type="submit"
                  disabled={saving || loadingSecret}
                  style={{ ...styles.primaryButton, ...((saving || loadingSecret) ? styles.buttonDisabled : {}) }}
                >
                  {saving ? 'Saving…' : editing ? 'Save Changes' : 'Create Key'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete confirm */}
      {confirmDelete && (
        <div style={styles.modal} onClick={() => setConfirmDelete(null)}>
          <div style={{ ...styles.modalContent, maxWidth: '420px' }} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h2 style={styles.modalTitle}>Delete Payment Key?</h2>
            </div>
            <div style={styles.modalBody}>
              <p style={{ fontSize: '14px', color: '#475569', margin: 0 }}>
                This will permanently delete the key for "<strong>{confirmDelete.appName}</strong>". This action cannot be undone.
              </p>
            </div>
            <div style={styles.modalFooter}>
              <button type="button" onClick={() => setConfirmDelete(null)} style={styles.secondaryButton}>Cancel</button>
              <button
                type="button"
                onClick={handleDelete}
                style={{ ...styles.primaryButton, backgroundColor: '#dc2626' }}
              >Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PaymentKeys;
